import type { DateTime } from "luxon";
import type { FailedTab, PromptHistoryFileType } from "../../hooks";
import type { LoadRecurringResult, LoadShiftsResult } from "../LoadResultDisplay";
import type { LoadNursesResults } from "../NursesSummary";
import type { PreviewFileType } from "../PreviewCard";

/** What a file is. `null` until the onboarder picks one — see `DEFAULT_KIND` below. */
export type DataLoadFileKind = "roster" | "shifts" | "template";

/** Whether the file goes through the LLM transform first, or uploads byte-for-byte. */
export type DataLoadMode = "transform" | "direct";

/** Only read when `kind === "roster"`. Picks between the create and update loaders. */
export type RosterAction = "create" | "update";

export type DataLoadPhase = "configure" | "results";

export type DataLoadFile = {
	id: string;
	file: File;

	// ---- the onboarder's choices (configure phase) ----
	/**
	 * Deliberately starts unset, unlike Auto Prompt which defaults to "roster". A forgotten dropdown
	 * on a mixed drop would otherwise send a shifts file to the nurses loader, which is the most
	 * destructive misconfiguration this flow allows. Run stays disabled until every file has a kind.
	 */
	kind: DataLoadFileKind | null;
	mode: DataLoadMode;
	/**
	 * Kept on every file rather than in a union keyed on `kind`, so flipping kind back and forth does
	 * not lose a choice the onboarder already made.
	 */
	rosterAction: RosterAction;
	/**
	 * Per file, not per kind: two rosters from the same client can need different instructions, and
	 * the prompt-history row this run writes is already one-per-file on the backend.
	 */
	prompt: string;
	/**
	 * Latches on the first keystroke in this file's prompt and never clears. Blocks every re-seed
	 * from that point on — without it a late history fetch or a kind change silently discards what
	 * the onboarder wrote.
	 */
	promptDirty: boolean;
	/** Per Excel sheet. Transform mode only; empty for CSVs and single-sheet workbooks. */
	tabInstructions: Record<string, string>;
	sheetNames: string[];

	// ---- run outputs ----
	/** Row in `onboarding_prompt_history`; travels into the load call so the row is marked loaded. */
	promptHistoryId: string | null;
	/**
	 * `null` in direct mode (nothing is transformed) AND on transform failure — `transformError` is
	 * what tells those two apart.
	 */
	transformedData: ArrayBuffer | null;
	failedTabs: FailedTab[];
	/** PreviewCard's filtered/sorted payload. Transform mode only; null until the card reports one. */
	filteredBlob: Blob | null;
	/**
	 * Earliest date in the payload that will actually upload (shifts only). Stored rather than
	 * derived during render: these buffers reach tens of MB, and the preview filter can drop a file's
	 * earliest rows, which changes where that file belongs in the load order.
	 */
	earliestDate: DateTime | null;
	/** The "Skip on upload" switch on the card. */
	skip: boolean;
	loadResult: LoadShiftsResult | LoadRecurringResult | LoadNursesResults | null;
	/** Kept rather than dropping the file, so a mixed batch shows WHICH file failed and why. */
	transformError: string | null;
	/**
	 * Without this a failed load is indistinguishable from one nobody attempted — both are
	 * `loadResult === null` — and the only trace is a snackbar that has already gone.
	 */
	loadError: string | null;
};

export const KIND_LABEL: Record<DataLoadFileKind, string> = {
	roster: "Roster",
	shifts: "Shifts",
	template: "Template",
};

export const MODE_LABEL: Record<DataLoadMode, string> = {
	transform: "Transform & upload",
	direct: "Upload as-is",
};

export const ROSTER_ACTION_LABEL: Record<RosterAction, string> = {
	create: "Create",
	update: "Update",
};

/**
 * Fixed load order: Roster -> Shifts -> Template. Load-bearing, not cosmetic: shifts name-match
 * against the roster already in the DB, and a template reconciles its occurrences against the
 * loaded shifts (`build_nurse_shift_windows`).
 */
export const KIND_ORDER: readonly DataLoadFileKind[] = ["roster", "shifts", "template"];

/** Our "roster" kind is the backend's "nurses" file type. Shifts and template pass through. */
export const fileTypeOf = (kind: DataLoadFileKind): PromptHistoryFileType & PreviewFileType => (kind === "roster" ? "nurses" : kind);

export const isExcelFile = (file: File): boolean => [".xls", ".xlsx", ".xlsm"].some((ext) => file.name.toLowerCase().endsWith(ext));

export const newDataLoadFile = (file: File): DataLoadFile => ({
	id: crypto.randomUUID(),
	file,
	kind: null,
	mode: "transform",
	// DEFAULT: create — loading a department's first roster is the overwhelmingly common case, and
	// unlike `kind` this choice is never invisible: the Create/Update switch renders pre-selected on
	// every roster row, so the value the run will use is on screen before Run is pressed.
	rosterAction: "create",
	prompt: "",
	promptDirty: false,
	tabInstructions: {},
	sheetNames: [],
	promptHistoryId: null,
	transformedData: null,
	failedTabs: [],
	filteredBlob: null,
	earliestDate: null,
	skip: false,
	loadResult: null,
	transformError: null,
	loadError: null,
});

export const formatFileSize = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};
